"use client";

import {createContext, type ReactNode, useContext, useEffect, useMemo, useState,} from "react";
import {inter, JetBrainsMono, sfProDisplay} from "@/utils/fonts";

export type LandingTheme = "light" | "dark";

type LandingThemeContextValue = {
  theme: LandingTheme;
  setTheme: (theme: LandingTheme) => void;
  toggleTheme: () => void;
};

const LandingThemeContext = createContext<LandingThemeContextValue>({
  theme: "light",
  setTheme: () => undefined,
  toggleTheme: () => undefined,
});

const STORAGE_KEY = "reputech-landing-theme";

export function LandingThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<LandingTheme>("light");
  const [themeInitialized, setThemeInitialized] = useState(false);

  useEffect(() => {
    const savedTheme = window.localStorage.getItem(STORAGE_KEY);
    if (savedTheme === "light" || savedTheme === "dark") {
      setTheme(savedTheme);
      setThemeInitialized(true);
      return;
    }

    // Disabled for now once the dark mode design is complete.
    // if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
    //   setTheme("dark");
    // }
    setThemeInitialized(true);
  }, []);

  useEffect(() => {
    if (!themeInitialized) {
      return;
    }

    window.localStorage.setItem(STORAGE_KEY, theme);
    document.documentElement.style.colorScheme = theme;

    return () => {
      document.documentElement.style.removeProperty("color-scheme");
    };
  }, [theme, themeInitialized]);

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      toggleTheme: () =>
        setTheme((currentTheme) =>
          currentTheme === "light" ? "dark" : "light",
        ),
    }),
    [theme],
  );

  return (
    <LandingThemeContext.Provider value={value}>
      <div
        className={`rh-landing-shell min-w-0 overflow-clip bg-[var(--bg)] text-[var(--text)] tracking-normal [font-family:var(--font-sf-pro-display),var(--font-inter),Inter,Arial,sans-serif] ${sfProDisplay.variable} ${inter.variable} ${JetBrainsMono.variable}`}
        data-theme={theme}
      >
        {children}
      </div>
    </LandingThemeContext.Provider>
  );
}

export const useLandingTheme = () => useContext(LandingThemeContext);
